import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertCircle, Wrench, Receipt, Boxes, UserPlus, GraduationCap, Info, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICONS = {
  loan_overdue: AlertCircle,
  maintenance_due: Wrench,
  bulk_bill_ready: Receipt,
  low_inventory: Boxes,
  new_staff: UserPlus,
  new_student: GraduationCap,
  info: Info,
};

const TYPE_COLORS = {
  loan_overdue: 'text-red-600 bg-red-100 dark:bg-red-900/30',
  maintenance_due: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30',
  bulk_bill_ready: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30',
  low_inventory: 'text-amber-600 bg-amber-100 dark:bg-amber-900/30',
  new_staff: 'text-green-600 bg-green-100 dark:bg-green-900/30',
  new_student: 'text-green-600 bg-green-100 dark:bg-green-900/30',
  info: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30',
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/unread-count`);
      setUnread(res.data.unread || 0);
    } catch (_) {
      /* silent */
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/notifications?limit=30`);
      setItems(res.data);
    } catch (_) {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    const id = setInterval(fetchUnread, 60_000);
    return () => clearInterval(id);
  }, [fetchUnread]);

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  const markRead = async (notifId, e) => {
    e?.stopPropagation();
    try {
      await axios.post(`${API_URL}/api/notifications/${notifId}/read`);
      setItems((curr) => curr.map((n) => (n.id === notifId ? { ...n, is_read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) { /* noop */ }
  };

  const markAllRead = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/notifications/read-all`);
      setItems((curr) => curr.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
      toast.success(`Marked ${res.data.marked || 0} as read`);
    } catch (_) {
      toast.error('Failed');
    }
  };

  const onClickItem = (n) => {
    if (!n.is_read) markRead(n.id);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 min-w-5 px-1 text-xs flex items-center justify-center bg-red-500 text-white"
              data-testid="notification-unread-badge"
            >
              {unread > 99 ? '99+' : unread}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notification-popover">
        <div className="flex items-center justify-between p-3 border-b">
          <div>
            <p className="font-semibold">Notifications</p>
            <p className="text-xs text-muted-foreground">{unread} unread</p>
          </div>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllRead}
              data-testid="mark-all-read-btn"
            >
              <Check className="h-3.5 w-3.5 mr-1" /> Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-96">
          {loading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
              No notifications yet
            </div>
          ) : (
            <div className="divide-y">
              {items.map((n) => {
                const Icon = TYPE_ICONS[n.type] || Info;
                return (
                  <button
                    key={n.id}
                    onClick={() => onClickItem(n)}
                    className={cn(
                      'w-full text-left p-3 hover:bg-muted/50 transition-colors flex items-start gap-3',
                      !n.is_read && 'bg-primary/5'
                    )}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className={cn('p-2 rounded-lg flex-shrink-0', TYPE_COLORS[n.type] || TYPE_COLORS.info)}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={cn('text-sm', !n.is_read && 'font-semibold')}>
                          {n.title}
                        </p>
                        {!n.is_read && (
                          <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {(() => {
                          try {
                            return formatDistanceToNow(new Date(n.created_at), { addSuffix: true });
                          } catch (e) {
                            return n.created_at;
                          }
                        })()}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
